/**
 * 🔍 ENHANCED USER FILTERS - Advanced filtering with saved presets
 * Provides comprehensive filtering options for admin user management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Stack,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Divider,
  Badge,
} from '@mui/material';
import {
  Search,
  FilterList,
  ExpandMore,
  Save,
  Bookmark,
  Delete,
  Edit,
  Clear,
  Today,
  DateRange,
  LocationOn,
  Person,
  Schedule,
  Verified,
  Warning,
  Star,
  Refresh,
} from '@mui/icons-material';

export interface FilterOptions {
  search: string;
  roleFilter: string;
  statusFilter: string;
  verificationFilter: string;
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  location: {
    country: string;
    city: string;
  };
  completionRange: {
    min: number;
    max: number;
  };
  lastActivityDays: number | null;
  hasMultipleRoles: boolean | null;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface SavedFilter {
  id: string;
  name: string;
  description?: string;
  filters: FilterOptions;
  createdAt: Date;
  usage: number;
}

interface EnhancedUserFiltersProps {
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  onApplyFilters: () => void;
  userCount: number;
  isLoading?: boolean;
}

const QUICK_FILTERS = [
  { id: 'today', label: 'Registered Today', icon: <Today /> },
  { id: 'pending', label: 'Pending Review', icon: <Schedule /> },
  { id: 'verified', label: 'Verified Users', icon: <Verified /> },
  { id: 'multi-role', label: 'Multi-Role Users', icon: <Star /> },
  { id: 'incomplete', label: 'Incomplete Profiles', icon: <Warning /> },
  { id: 'recent', label: 'Active This Week', icon: <Person /> },
];

const AFRICAN_COUNTRIES_CITIES = {
  'Nigeria': [
    'Lagos', 'Abuja', 'Kano', 'Ibadan', 'Port Harcourt', 'Benin City',
    'Maiduguri', 'Zaria', 'Aba', 'Jos', 'Ilorin', 'Oyo', 'Enugu',
    'Kaduna', 'Katsina', 'Bauchi', 'Akure', 'Makurdi', 'Sokoto', 'Gombe'
  ],
  'Kenya': [
    'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika',
    'Malindi', 'Naivasha', 'Machakos', 'Meru', 'Nyeri', 'Kitale'
  ],
  'South Africa': [
    'Johannesburg', 'Cape Town', 'Durban', 'Pretoria', 'Port Elizabeth',
    'Bloemfontein', 'East London', 'Polokwane', 'Nelspruit', 'Kimberley'
  ],
  'Ghana': [
    'Accra', 'Kumasi', 'Tamale', 'Takoradi', 'Sunyani', 'Cape Coast',
    'Ho', 'Koforidua', 'Wa', 'Bolgatanga', 'Tema', 'Techiman'
  ],
  'Egypt': [
    'Cairo', 'Alexandria', 'Giza', 'Shubra El Kheima', 'Port Said',
    'Suez', 'Luxor', 'Aswan', 'Hurghada', 'Mansoura', 'Tanta', 'Faiyum'
  ],
  'Rwanda': [
    'Kigali', 'Butare', 'Gitarama', 'Ruhengeri', 'Gisenyi', 'Byumba',
    'Cyangugu', 'Kibuye', 'Rwamagana', 'Kibungo', 'Nyanza'
  ],
  'Tanzania': [
    'Dar es Salaam', 'Mwanza', 'Arusha', 'Dodoma', 'Mbeya', 'Morogoro',
    'Tanga', 'Zanzibar', 'Kigoma', 'Moshi', 'Tabora', 'Iringa'
  ],
  'Uganda': [
    'Kampala', 'Gulu', 'Lira', 'Mbarara', 'Jinja', 'Bwizibwera',
    'Mbale', 'Mukono', 'Kasese', 'Masaka', 'Entebbe', 'Tororo'
  ],
  'Morocco': [
    'Casablanca', 'Rabat', 'Fez', 'Marrakesh', 'Tangier', 'Agadir',
    'Meknes', 'Oujda', 'Kenitra', 'Tetouan', 'Safi', 'El Jadida'
  ],
  'Ethiopia': [
    'Addis Ababa', 'Dire Dawa', 'Mekelle', 'Bahir Dar', 'Gondar',
    'Awassa', 'Dessie', 'Jimma', 'Harar', 'Shashemene', 'Debre Markos'
  ],
  'Senegal': [
    'Dakar', 'Thies', 'Saint-Louis', 'Kaolack', 'Mbour', 'Ziguinchor',
    'Rufisque', 'Diourbel', 'Louga', 'Tambacounda', 'Richard Toll'
  ],
  'Ivory Coast': [
    'Abidjan', 'Bouake', 'Daloa', 'Yamoussoukro', 'San-Pedro', 'Divo',
    'Korhogo', 'Anyama', 'Abengourou', 'Man', 'Gagnoa', 'Soubre'
  ]
};

export const EnhancedUserFilters: React.FC<EnhancedUserFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  userCount,
  isLoading = false
}) => {
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [presetMenuAnchor, setPresetMenuAnchor] = useState<null | HTMLElement>(null);
  const [newFilterName, setNewFilterName] = useState('');
  const [newFilterDescription, setNewFilterDescription] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<Set<string>>(new Set());

  // Load saved filters from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('admin-user-filters');
      if (saved) {
        const parsed = JSON.parse(saved).map((filter: any) => ({
          ...filter,
          createdAt: new Date(filter.createdAt)
        }));
        setSavedFilters(parsed);
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
    }
  }, []);

  // Save filters to localStorage
  const saveFiltersToStorage = (filters: SavedFilter[]) => {
    try {
      localStorage.setItem('admin-user-filters', JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  };

  const handleQuickFilter = (quickFilterId: string) => {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Toggle the quick filter selection
    const newSelected = new Set(selectedQuickFilters);
    if (newSelected.has(quickFilterId)) {
      newSelected.delete(quickFilterId);
      // Remove the filter when deselecting
      onFiltersChange({
        ...filters,
        dateRange: { start: null, end: null },
        verificationFilter: 'all',
        statusFilter: 'all',
        hasMultipleRoles: null,
        completionRange: { min: 0, max: 100 },
        lastActivityDays: null
      });
    } else {
      newSelected.add(quickFilterId);

      let newFilters: Partial<FilterOptions> = {};

      switch (quickFilterId) {
        case 'today':
          newFilters = {
            dateRange: { start: today, end: today }
          };
          break;
        case 'pending':
          newFilters = {
            verificationFilter: 'pending',
            statusFilter: 'pending'
          };
          break;
        case 'verified':
          newFilters = {
            verificationFilter: 'verified',
            statusFilter: 'active'
          };
          break;
        case 'multi-role':
          newFilters = {
            hasMultipleRoles: true
          };
          break;
        case 'incomplete':
          newFilters = {
            completionRange: { min: 0, max: 70 }
          };
          break;
        case 'recent':
          newFilters = {
            lastActivityDays: 7
          };
          break;
      }

      onFiltersChange({ ...filters, ...newFilters });
    }

    setSelectedQuickFilters(newSelected);
  };

  const handleSaveFilter = () => {
    if (!newFilterName.trim()) return;

    const newFilter: SavedFilter = {
      id: `filter_${Date.now()}`,
      name: newFilterName.trim(),
      description: newFilterDescription.trim() || undefined,
      filters: { ...filters },
      createdAt: new Date(),
      usage: 0
    };

    const updatedFilters = [...savedFilters, newFilter];
    setSavedFilters(updatedFilters);
    saveFiltersToStorage(updatedFilters);

    setSaveDialogOpen(false);
    setNewFilterName('');
    setNewFilterDescription('');
  };

  const handleLoadPreset = (preset: SavedFilter) => {
    onFiltersChange(preset.filters);
    
    // Update usage count
    const updatedFilters = savedFilters.map(filter =>
      filter.id === preset.id ? { ...filter, usage: filter.usage + 1 } : filter
    );
    setSavedFilters(updatedFilters);
    saveFiltersToStorage(updatedFilters);
    setPresetMenuAnchor(null);
  };

  const handleDeletePreset = (presetId: string) => {
    const updatedFilters = savedFilters.filter(filter => filter.id !== presetId);
    setSavedFilters(updatedFilters);
    saveFiltersToStorage(updatedFilters);
  };

  const handleClearAllFilters = () => {
    const defaultFilters: FilterOptions = {
      search: '',
      roleFilter: 'all',
      statusFilter: 'all',
      verificationFilter: 'all',
      dateRange: { start: null, end: null },
      location: '',
      completionRange: { min: 0, max: 100 },
      lastActivityDays: null,
      hasMultipleRoles: null,
      sortBy: 'registeredAt',
      sortOrder: 'desc'
    };
    onFiltersChange(defaultFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.roleFilter !== 'all') count++;
    if (filters.statusFilter !== 'all') count++;
    if (filters.verificationFilter !== 'all') count++;
    if (filters.dateRange.start || filters.dateRange.end) count++;
    if (filters.location.country || filters.location.city) count++;
    if (filters.completionRange.min > 0 || filters.completionRange.max < 100) count++;
    if (filters.lastActivityDays) count++;
    if (filters.hasMultipleRoles !== null) count++;
    return count;
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          {/* Header with Quick Actions */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                <FilterList />
                Advanced Filters
                {getActiveFilterCount() > 0 && (
                  <Badge badgeContent={getActiveFilterCount()} color="primary" />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userCount} users found
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={<Bookmark />}
                onClick={(e) => setPresetMenuAnchor(e.currentTarget)}
                variant="outlined"
                size="small"
              >
                Presets ({savedFilters.length})
              </Button>
              <Button
                startIcon={<Save />}
                onClick={() => setSaveDialogOpen(true)}
                variant="outlined"
                size="small"
                disabled={getActiveFilterCount() === 0}
              >
                Save Filter
              </Button>
              <Button
                startIcon={<Clear />}
                onClick={handleClearAllFilters}
                variant="outlined"
                size="small"
                disabled={getActiveFilterCount() === 0}
              >
                Clear All
              </Button>
              <Button
                startIcon={<Refresh />}
                onClick={onApplyFilters}
                variant="contained"
                size="small"
                disabled={isLoading}
              >
                Apply
              </Button>
            </Stack>
          </Box>

          {/* Quick Filters */}
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>Quick Filters</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {QUICK_FILTERS.map((filter) => (
                <Chip
                  key={filter.id}
                  icon={filter.icon}
                  label={filter.label}
                  onClick={() => handleQuickFilter(filter.id)}
                  variant={selectedQuickFilters.has(filter.id) ? "filled" : "outlined"}
                  color={selectedQuickFilters.has(filter.id) ? "primary" : "default"}
                  size="small"
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' },
                    fontWeight: selectedQuickFilters.has(filter.id) ? 600 : 400
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Basic Filters */}
          <Accordion 
            expanded={expandedSections.has('basic')}
            onChange={() => toggleSection('basic')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Basic Filters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Search"
                    placeholder="Name, email, phone, or ID..."
                    value={filters.search}
                    onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={filters.roleFilter}
                      label="Role"
                      onChange={(e) => onFiltersChange({ ...filters, roleFilter: e.target.value })}
                    >
                      <MenuItem value="all">All Roles</MenuItem>
                      <MenuItem value="renter">Renter Only</MenuItem>
                      <MenuItem value="host">Host Only</MenuItem>
                      <MenuItem value="driver">Driver Only</MenuItem>
                      <MenuItem value="multi-role">Multi-Role Users</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.statusFilter}
                      label="Status"
                      onChange={(e) => onFiltersChange({ ...filters, statusFilter: e.target.value })}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                      <MenuItem value="banned">Banned</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Advanced Filters */}
          <Accordion 
            expanded={expandedSections.has('advanced')}
            onChange={() => toggleSection('advanced')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Advanced Filters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Verification Status</InputLabel>
                    <Select
                      value={filters.verificationFilter}
                      label="Verification Status"
                      onChange={(e) => onFiltersChange({ ...filters, verificationFilter: e.target.value })}
                    >
                      <MenuItem value="all">All Verification</MenuItem>
                      <MenuItem value="verified">Verified</MenuItem>
                      <MenuItem value="pending">Pending Review</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="incomplete">Incomplete</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={filters.location.country}
                      label="Country"
                      onChange={(e) => onFiltersChange({ 
                        ...filters, 
                        location: { ...filters.location, country: e.target.value, city: '' } 
                      })}
                    >
                      <MenuItem value="">All Countries</MenuItem>
                      {Object.keys(AFRICAN_COUNTRIES_CITIES).map((country) => (
                        <MenuItem key={country} value={country}>{country}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" disabled={!filters.location.country}>
                    <InputLabel>
                      {!filters.location.country ? 'City (Select Country First)' : 'City'}
                    </InputLabel>
                    <Select
                      value={filters.location.city}
                      label={!filters.location.country ? 'City (Select Country First)' : 'City'}
                      onChange={(e) => onFiltersChange({ 
                        ...filters, 
                        location: { ...filters.location, city: e.target.value } 
                      })}
                    >
                      <MenuItem value="">All Cities</MenuItem>
                      {filters.location.country && AFRICAN_COUNTRIES_CITIES[filters.location.country]?.map((city) => (
                        <MenuItem key={city} value={city}>{city}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Last Activity</InputLabel>
                    <Select
                      value={filters.lastActivityDays || ''}
                      label="Last Activity"
                      onChange={(e) => onFiltersChange({ 
                        ...filters, 
                        lastActivityDays: e.target.value ? Number(e.target.value) : null 
                      })}
                    >
                      <MenuItem value="">Any Time</MenuItem>
                      <MenuItem value={1}>Last 24 hours</MenuItem>
                      <MenuItem value={7}>Last 7 days</MenuItem>
                      <MenuItem value={30}>Last 30 days</MenuItem>
                      <MenuItem value={90}>Last 90 days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Date Range Filters */}
          <Accordion 
            expanded={expandedSections.has('dates')}
            onChange={() => toggleSection('dates')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Date Range</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Registration From"
                    type="date"
                    value={filters.dateRange.start ? filters.dateRange.start.toISOString().split('T')[0] : ''}
                    onChange={(e) => onFiltersChange({
                      ...filters,
                      dateRange: { 
                        ...filters.dateRange, 
                        start: e.target.value ? new Date(e.target.value) : null 
                      }
                    })}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Registration To"
                    type="date"
                    value={filters.dateRange.end ? filters.dateRange.end.toISOString().split('T')[0] : ''}
                    onChange={(e) => onFiltersChange({
                      ...filters,
                      dateRange: { 
                        ...filters.dateRange, 
                        end: e.target.value ? new Date(e.target.value) : null 
                      }
                    })}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Preset Menu */}
      <Menu
        anchorEl={presetMenuAnchor}
        open={Boolean(presetMenuAnchor)}
        onClose={() => setPresetMenuAnchor(null)}
      >
        {savedFilters.length === 0 ? (
          <MenuItem disabled>
            <ListItemText>No saved filters</ListItemText>
          </MenuItem>
        ) : (
          savedFilters.map((preset) => (
            <MenuItem key={preset.id} onClick={() => handleLoadPreset(preset)}>
              <ListItemIcon>
                <Bookmark />
              </ListItemIcon>
              <ListItemText
                primary={preset.name}
                secondary={`Used ${preset.usage} times`}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePreset(preset.id);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Save Filter Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Filter Preset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Filter Name"
              value={newFilterName}
              onChange={(e) => setNewFilterName(e.target.value)}
              placeholder="e.g., Pending Driver Applications"
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              value={newFilterDescription}
              onChange={(e) => setNewFilterDescription(e.target.value)}
              placeholder="Brief description of what this filter shows..."
              multiline
              rows={2}
            />
            <Alert severity="info">
              This filter will be saved locally and available for quick access.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveFilter}
            variant="contained"
            disabled={!newFilterName.trim()}
          >
            Save Filter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};